import { Flex, useStyles } from '@archipro-design/aria';

import * as S from '@modules/design-board/component/manage/collaborator-overview/CollaboratorsOverview.style';
import { CollaboratorAvatar } from '@modules/design-board/component/manage/collaborator-overview/components/CollaboratorAvatar';
import { ResendInviteLink } from '@modules/design-board/component/manage/collaborator-overview/components/ResendInviteLink';
import { ActionMenu } from '@modules/design-board/component/manage/collaborator-overview/components/ActionMenu';
import { CollaboratorAccessEnumTitleMap } from '@modules/design-board/validation/schemas';
import classNames from 'classnames';
import { SHOW_COMMENT } from '~/modules/design-board/config/design-board-config';
import { useFetcher } from '@remix-run/react';
import { useDesignBoardPageState } from '@modules/design-board/component/context';
import {AllowedActions} from "@modules/design-board/actions/handler-mappings";
import {useLogger} from "@archipro-website/logger/client";

interface CollaboratorProps {
    name: string;
    imageUrl: string;
    membership: 'owners' | 'collaborators';
    inviteStatus: 'accepted' | 'sent';
    email?: string;
    access: 'edit' | 'view' | 'comment';
}

export const Collaborator = ({
    name,
    imageUrl,
    membership,
    inviteStatus,
    email,
    access,
}: CollaboratorProps) => {
    const { css } = useStyles();
    const logger = useLogger();
    const fetcher = useFetcher();
    const {
        state: { currentBoardData },
    } = useDesignBoardPageState();

    const renderMode =
        membership === 'owners'
            ? 'owner'
            : membership === 'collaborators' && inviteStatus === 'accepted'
            ? 'collaborator'
            : 'invited';

    const changePermission = (permission: string) => {
        if (currentBoardData && email) {
            fetcher.submit(
                {
                designBoardId: String(currentBoardData.ID),
                email: email,
                canEdit: String(permission === 'edit'),
                hasAccepted: String(inviteStatus === 'accepted'),
                _action: AllowedActions.enum.update_collaborator_permission,
            },
            {
                method: 'post',
                action: location.pathname,
            });
        }
        else{
            logger.error('attempted to permission change without user data');
        }
    };

    switch (renderMode) {
        case 'owner': {
            return (
                <div className={css(S.OwnerGridContainer, S.CollaboratorRoot)}>
                    <CollaboratorAvatar name={name} imageUrl={imageUrl} />
                    <Flex
                        vAlign={'center'}
                        hAlign={'end'}
                        design={{
                            width: '100%',
                        }}
                    >
                        <p>Owner</p>
                    </Flex>
                </div>
            );
        }
        case 'invited': {
            return email ? (
                <div
                    className={css(
                        S.CollaboratorRoot,
                        S.InvitedCollaboratorGrid
                    )}
                >
                    <Flex vAlign={'center'}>
                        <CollaboratorAvatar name={name} imageUrl={imageUrl} />
                    </Flex>
                    <Flex
                        vAlign={'center'}
                        hAlign={'center'}
                        design={{ width: '100%' }}
                    >
                        <ResendInviteLink email={email} />
                    </Flex>
                    <Flex
                        vAlign={'center'}
                        hAlign={'end'}
                        className={classNames(
                            css({
                                position: 'relative',
                            }),
                            'Collaborator__action'
                        )}
                    >
                        <ActionMenu
                            noChevron={true}
                            hug={'right'}
                            variant={'text'}
                            email={email}
                            access={access}
                            inviteStatus={inviteStatus}
                            options={[{ id: inviteStatus, render: 'Invited' }]}
                        />
                    </Flex>
                </div>
            ) : null;
        }
        case 'collaborator':
            const collabPermissionOptions = Object.keys(
                CollaboratorAccessEnumTitleMap
            )
                .filter((ca) => !(!SHOW_COMMENT && ca === 'comment'))
                .map((permission) => {
                    return {
                        render: `${CollaboratorAccessEnumTitleMap[permission]}`,
                        onClick: () =>
                            changePermission(permission),
                        id: permission,
                    };
                });

            return email ? (
                <div
                    className={css(
                        S.CollaboratorGridContainer,
                        S.CollaboratorRoot
                    )}
                >
                    <CollaboratorAvatar name={name} imageUrl={imageUrl} />
                    <Flex
                        hAlign={'end'}
                        vAlign={'center'}
                        design={{
                            width: '100%',
                        }}
                    >
                        <Flex
                            vAlign={'center'}
                            design={{
                                position: 'relative',
                                width: '100%',
                                height: '100%',
                            }}
                        >
                            <ActionMenu
                                hug={'right'}
                                noChevron={true}
                                email={email}
                                variant={'text'}
                                access={access}
                                inviteStatus={inviteStatus}
                                options={collabPermissionOptions}
                            />
                        </Flex>
                    </Flex>
                </div>
            ) : null;
    }
};
